
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { TagBadge } from "./TagBadge";
import { TagSelector } from "./TagSelector";
import { Tag } from "../types/categoryRules";

interface AppTagManagementProps {
  appId: string;
  categoryName?: string;
  currentTagIds: string[];
  onTagsUpdate: (newTagIds: string[]) => void;
}

export const AppTagManagement = ({ 
  appId, 
  categoryName, 
  currentTagIds, 
  onTagsUpdate 
}: AppTagManagementProps) => {
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);
  const { toast } = useToast();

  // Fetch all categories from Convex and find the one we need
  const categoriesData = useQuery(api.appCategories.list.categories);

  const updateAppTags = useMutation(api.apps.update.tags);

  // Find the specific category we're looking for
  const categoryData = categoriesData?.find(
    (cat: any) => cat.name === categoryName
  );

  // Transform Convex category data to get available tags - use simple tag names as IDs
  const availableTags: Tag[] = categoryData?.tags ? 
    categoryData.tags.map((tagName: string) => ({
      id: tagName, // Use simple tag name as ID
      name: tagName,
      color: "#3B82F6", // Default blue color for all tags
      category_id: categoryData.name || "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })) : [];

  // Get current tag details based on tag names - simplified approach
  const currentTags: Tag[] = currentTagIds.map((tagId: string) => ({
    id: tagId,
    name: tagId, // Tag ID is the tag name itself
    color: "#3B82F6",
    category_id: categoryData?.name || "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  const handleAddTag = async (tagId: string) => {
    const newTagIds = [...currentTagIds, tagId];
    try {
      await updateAppTags({
        app: appId as any, // The Convex API expects an Id<"apps"> type
        tags: newTagIds,
      });
      
      // Update local state immediately
      onTagsUpdate(newTagIds);
      setIsTagSelectorOpen(false);
      toast({
        title: "Success",
        description: "App tags updated successfully",
      });
    } catch (error) {
      console.error('Error updating app tags:', error);
      toast({
        title: "Error",
        description: "Failed to update app tags",
        variant: "destructive",
      });
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    const newTagIds = currentTagIds.filter(id => id !== tagId);
    try {
      await updateAppTags({
        app: appId as any, // The Convex API expects an Id<"apps"> type
        tags: newTagIds,
      });
      
      // Update local state immediately
      onTagsUpdate(newTagIds);
      toast({
        title: "Success",
        description: "Tag removed successfully",
      });
    } catch (error) {
      console.error('Error updating app tags:', error);
      toast({
        title: "Error",
        description: "Failed to remove tag",
        variant: "destructive",
      });
    }
  };

  if (!categoryName) {
    return (
      <div className="text-xs text-gray-400">
        No category assigned - cannot manage tags
      </div>
    );
  }

  if (!categoriesData) {
    return (
      <div className="text-xs text-gray-400">
        Loading category data...
      </div>
    );
  }

  if (!categoryData) {
    return (
      <div className="text-xs text-gray-400">
        Category "{categoryName}" not found
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 items-center">
        {currentTags.map((tag) => (
          <TagBadge
            key={tag.id}
            tag={tag}
            onRemove={() => handleRemoveTag(tag.id)}
          />
        ))}
        
        <TagSelector
          tags={availableTags}
          selectedTagIds={currentTagIds}
          onToggleTag={handleAddTag}
          isOpen={isTagSelectorOpen}
          onToggle={() => setIsTagSelectorOpen(!isTagSelectorOpen)}
        />
      </div>
      
      {availableTags.length === 0 && (
        <div className="text-xs text-gray-400">
          No tags available for {categoryName} category
        </div>
      )}
    </div>
  );
};
